import React, { PureComponent } from "react";
import { Link } from "react-router";
import { Loader } from "react-loaders";
import { find, flatten, isNil, map, some, pick, fromPairs, zip } from "lodash";
import { pure } from "recompose";
import styled from 'styled-components';

import { changeLocation } from '../../api/Location';
import { getRequest, batchRequest } from "../../api/ApiService";
import { fetchCompetitionMetrics, fetchTeamMetrics } from "../../api/MetricsService";
import { fetchAllStaticData } from "../../api/StaticDataService";
import { fetchTeamDecisions } from "../../api/TeamDecisionsService";
import { fetchFinancialStatements } from "../../api/FinancialStatementsService";
import { logError } from "../../services/utils";
import Auth from "routes/Auth";
import { getTeam, getCompetitionTeams } from "./utils";
import { fontFamily } from "./styles";
import Paper from "./Paper";
import Menu from "./Menu";
import TeamReports from "./TeamReports";
import GradesRanks from "./GradesRanks";
import MarketShare from "./MarketShare";
import Revenues from "./Revenues";
import GrossMargins from "./GrossMargins";
import SharePrice from "./SharePrice";
import EarningsPerShare from "./EarningsPerShare";
import ReturnOnAssets from "./ReturnOnAssets";
import ReturnOnEquity from "./ReturnOnEquity";
import FPA from "./FPA";
import FPAIncomeStatement from "./FPAIncomeStatement";
import LeadTime from "./LeadTime";
import MarketingAnalysis from "./MarketingAnalysis";
import MarketingAnalysis1 from "./MarketingAnalysis1";
import MarketingNoUnitsSold1 from "./MarketingNoUnitsSold1";
import MarketingNoUnitsSold2 from "./MarketingNoUnitsSold2";
import MarketingPositioning from "./MarketingPositioning";
import MarketingSellingPrice from "./MarketingSellingPrice";
import MarketingAwareness from "./MarketingAwareness";
import MarketingChannelSpend from "./MarketingChannelSpend";
import OperationalAnalysis from "./OperationalAnalysis";
import UnitCosts1 from "./UnitCosts1";
import UnitCosts2 from "./UnitCosts2";
import InventoryManagement from "./InventoryManagement";
import OAFinancialImpact from "./OAFinancialImpact";
import FinancingAnalysis from "./FinancingAnalysis";
import CapitalStructure from "./CapitalStructure";
import CostOfDebt from "./CostOfDebt";
import ImpactOfDebt from "./ImpactOfDebt";
import CostOfCapital from "./CostOfCapital";
import Valuation from "./Valuation";
import Shares from "./Shares";
import FinancialStatements from "./FinancialStatements";
import IncomeStatement from "./IncomeStatement";
import BalanceSheet from "./BalanceSheet";
import CashFlows from "./CashFlows";
import Teams, { persistTeamColorIndexMap, teamsEndpoint } from "../../api/Teams";

import IconLock from "../../containers/assets/icon-lock.svg";

const RoundLockedScreen = {};
RoundLockedScreen.Wrapper = styled.div`
  height: 550px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
  margin: 24px 50px;
  width: calc(100% - 100px);
  font-family: Lato;
  line-height: 100%;
`;
RoundLockedScreen.Image = styled.img`
  margin-bottom: 24px;
  width: 27px;
`;
RoundLockedScreen.Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 32px;
`;
RoundLockedScreen.Text = styled.h3`
  font-size: 18px;
`;

const CrumbSeparator = pure(() => (
  <span
    style={{
      marginLeft: "1ch",
      marginRight: "1ch",
      fontSize: "12px",
      fontFamily,
      color: "#828282"
    }}
  >
    >
  </span>
));

const BreadCrumbs = pure(({ crumbs }) => (
  <div style={{ marginTop: "1em", marginBottom: "1em" }}>
    {flatten(
      map(crumbs, ({ location, label }, index) => [
        index !== 0 ? (
          <CrumbSeparator key={`${location}:${label}-sep`} />
        ) : null,
        <Link
          to={location}
          style={{ fontSize: "12px", fontFamily, color: "#828282" }}
          key={`${location}:${label}`}
        >
          {label}
        </Link>
      ])
    )}
  </div>
));

// Remaining API calls not covered by team-decisions or financial-statements
const APICalls = gameId => [
  { url: "teams", key: "teams" },
  {
    url: `games/${gameId}/top_performers`,
    key: "topPerformers",
    round: 'attachCompetition',
  },
  { url: "v2/games/targets", key: "targets" }
];

const Content = (({ ifLoaded, active, hasAccess }) =>
  hasAccess === true ? (
    (
      {
        "": () =>
          ifLoaded(
            ["grades", "teamRank", "round", "isFinished", "location"],
            TeamReports
          ),
        "grades-ranks": () => ifLoaded(["round", "targets"], GradesRanks),
        "market-share": () =>
          ifLoaded(["teams", "competitionMetrics"], MarketShare),
        revenues: () => ifLoaded(["teams", "competitionMetrics"], Revenues),
        "gross-margins": () =>
          ifLoaded(["teams", "competitionMetrics"], GrossMargins),
        "share-price": () =>
          ifLoaded(["constants", "teams", "competitionMetrics"], SharePrice),
        "earnings-per-share": () =>
          ifLoaded(["teams", "competitionMetrics"], EarningsPerShare),
        "return-on-assets": () =>
          ifLoaded(["teams", "competitionMetrics"], ReturnOnAssets),
        "return-on-equity": () =>
          ifLoaded(["teams", "competitionMetrics"], ReturnOnEquity),
        "fp-a": () => <FPA />,
        "financial-planning-and-analysis": () =>
          ifLoaded(
            [
              "teams",
              "teamId",
              "incomeStatement",
              "financialAnalysis",
              "round",
              "rounds"
            ],
            FPAIncomeStatement
          ),
        "marketing-analysis": () => <MarketingAnalysis />,
        "market-share-capture": () =>
          ifLoaded(
            [
              "competitiveAnalysis",
              "competitiveAnalysisTeam",
              "teams",
              "totalDemand",
              "competitionMetrics"
            ],
            MarketingAnalysis1
          ),
        "low-sales-1": () =>
          ifLoaded(["financials", "round"], MarketingNoUnitsSold1),
        "low-sales-2": () => ifLoaded(["messages"], MarketingNoUnitsSold2),
        "customer-survey": () =>
          ifLoaded(
            [
              "competitiveAnalysis",
              "teamId",
              "topPerformers",
              "teams",
              "rounds",
              "round"
            ],
            MarketingPositioning
          ),
        "marketing-selling-price": () =>
          ifLoaded(
            [
              "competitiveAnalysis",
              "teamId",
              "unitSales",
              "teams",
              "products",
              "forecasts",
              "competitionMetrics",
              "rounds",
              "round"
            ],
            MarketingSellingPrice
          ),
        "marketing-awareness": () =>
          ifLoaded(
            [
              "incomeStatement",
              "competitiveAnalysis",
              "teamId",
              "teams",
              "competitionMetrics"
            ],
            MarketingAwareness
          ),
        "marketing-financial-impact-1": () =>
          ifLoaded(["roundId", "teams", "teamId"], MarketingChannelSpend),
        "operational-analysis": () => <OperationalAnalysis />,
        "operational-analysis-unit-costs": () =>
          ifLoaded(
            ["competitiveAnalysis", "teams", "teamId", "competitionMetrics"],
            UnitCosts1
          ),
        "operational-analysis-unit-costs-breakdown": () =>
          ifLoaded(
            ["manufactureCosts", "round", "rounds", "teamId"],
            UnitCosts2
          ),
        "operational-analysis-inventory-management": () =>
          ifLoaded(
            [
              "constants",
              "inventoryBalances",
              "competitiveAnalysis",
              "products",
              "teamId",
              "teams",
              "competitionMetrics",
              "rounds",
              "round"
            ],
            InventoryManagement
          ),
        "operational-analysis-lead-time": () =>
          ifLoaded(
            [
              "teamId",
              "teams",
              "competitionMetrics",
              "roundId"
            ],
            LeadTime
          ),
        "operational-analysis-financial-impact": () =>
          ifLoaded(["incomeStatement"], OAFinancialImpact),
        "financing-analysis": () =>
          ifLoaded(["competitionMetrics", "teamId"], FinancingAnalysis),
        "financing-analysis-capital-structure": () =>
          ifLoaded(
            ["teams", "competitionMetrics", "metrics"],
            CapitalStructure
          ),
        "financing-analysis-cost-of-debt": () =>
          ifLoaded(["metrics"], CostOfDebt),
        "financing-analysis-impact-of-debt": () =>
          ifLoaded(["incomeStatement"], ImpactOfDebt),
        "financing-analysis-cost-of-capital": () =>
          ifLoaded(["metrics", "teams", "competitionMetrics"], CostOfCapital),
        "financing-analysis-valuation": () =>
          ifLoaded(
            [
              "round",
              "teams",
              "competitiveAnalysis",
              "competitiveAnalysisAll",
              "competitionMetrics",
              "teamId",
              "comparableCompanies",
              "constants"
            ],
            Valuation
          ),
        "financing-analysis-shares": () =>
          ifLoaded(
            [
              "constants",
              "metrics",
              "teams",
              "competitionMetrics",
              "equities",
              "stockRepurchased",
              "preMoneys",
              "equityCumulative",
              "teamId",
              "round",
              "roundId"
            ],
            Shares
          ),
        "financial-statements": () => <FinancialStatements />,
        "income-statement": () =>
          ifLoaded([
            "incomeStatement",
            "round",
            "rounds"
          ], IncomeStatement),
        "balance-sheet": () => ifLoaded(["balanceSheet", "round", "constants"], BalanceSheet),
        "cash-flows": () => ifLoaded(["cashFlows", "round"], CashFlows)
      }[active] || (() => <Paper>Page Not Found</Paper>)
    )()
  ) : (
    <Paper>Page Not Found</Paper>
  )
);

const getQueryVariable = variable => {
  const temporaryUser = Auth.getTemporaryUser();
  if (temporaryUser && temporaryUser[variable]) {
    return temporaryUser[variable];
  }

  console.log("Temporary user doesn't have %s field", variable);
};

export default class Analysis extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.initialState,
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl
    };

    if (Auth.getPosition() === "Student") {
      const team = Teams.currentTeam();
      this.teamId = team ? team.id : 0;
    } else {
      this.teamId = parseInt(getQueryVariable("team_id"), 10);
    }
  }

  initialState = {
    //  team: null,
    grades: [],
    //  gradesLte: [],
    balanceSheet: null,
    competitiveAnalysis: null,
    competitiveAnalysisAll: null,
    competitionMetrics: null,
    metricsReports: null,
    incomeStatement: null,
    cashFlows: null,
    metrics: null,
    messages: null,
    projections: null,
    products: null,
    forecasts: null,
    promos: null,
    sales: null,
    financials: null,
    manufactureCosts: null,
    teamGrade: "",
    topPerformers: null,
    unitSales: null,
    ceo: null,
    equities: null,
    stockRepurchased: null,
    preMoneys: null,
    equityCumulative: null,
    comparableCompanies: null,
    inventoryBalances: null,
    teams: null,
    tmpTeamGrade: 0,
    tmpRankName: "",
    tmpRounds: []
  };

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.currentRound !== this.state.currentRound ||
      nextProps.roundInUrl !== this.state.roundInUrl
    ) {
      this.setState(
        {
          currentRound: nextProps.currentRound,
          roundInUrl: nextProps.roundInUrl,
        },
        () => {
          this.fetchData(this.getRound());
        }
      );
    } else if (
      (nextProps.rounds !== this.props.rounds &&
      (!this.props.rounds || this.props.rounds.length === 0) &&
      nextProps.rounds && nextProps.rounds.length > 0) ||
      (nextProps.teams !== this.props.teams &&
      (!this.props.teams || this.props.teams.length === 0) &&
      nextProps.teams && nextProps.teams.length > 0)
    ) {
      // Re-fetch if rounds or teams went from empty to populated (initial load)
      this.fetchData(this.getRound());
    }
  }

  fetchDataForInstructor = apiCalls => {
    const round = this.getRound();
    const roundId = this.getRoundId();
    // Use the picker's current round as-is for the financial-statements
    // fetch. The old `round + 1 > maxRound ? maxRound : round + 1` clamp
    // against props.rounds (which filters to finished rounds) was pulling
    // lte back to Round 1 whenever the instructor viewed a later year
    // that hadn't finished yet, making every statement render blank.
    const roundFinancial = Number(round) || 0;
    const gameId = parseInt(getQueryVariable("game_id"), 10) || Auth.getGameId();

    // Fetch team decisions data
    fetchTeamDecisions(round, 'team-reports', this.teamId)
      .then(data => {
        this.setState({
          manufactureCosts: data.manufacture_costs || [],
          inventoryBalances: data.inventory_balances || [],
          unitSales: data.unit_sales || [],
          products: data.products || [],
          forecasts: data.price_forecasts || [],
          equities: data.equities || [],
          stockRepurchased: data.repurchased_shares || data.stock_repurchased || [],
          preMoneys: data.pre_moneys || [],
          equityCumulative: data.equity_cumulative || [],
          ceo: data.decisions ? { team: data.decisions } : {},
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchTeamDecisions");
      });

    // Fetch financial statements
    fetchFinancialStatements({ roundLte: roundFinancial, teamId: this.teamId })
      .then(data => {
        const currentRoundNumber = Number(this.getRound()) || 0;
        const incomeStatement = data.incomeStatement.find(i => Number(i.Round) === currentRoundNumber) || null;
        const balanceSheet = data.balanceSheet.find(i => Number(i.Round) === currentRoundNumber) || null;
        const cashFlows = data.cashflow.find(i => Number(i.Round) === currentRoundNumber) || null;
        this.setState({
          incomeStatement,
          balanceSheet,
          cashFlows,
          projections: data.projections || [],
          financialAnalysis: data.financialAnalysis || [],
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchFinancialStatements");
      });

    // Fetch static data
    this.fetchStaticData(round);

    // Resolve the user's competition up front. Props are the primary
    // source (populated by App.js on load) but they're not available
    // for the student flow, so fall back to Teams.currentTeam() which
    // reads localStorage populated during login.
    const teamsFromProps = this.props.teams || [];
    const currentTeamFromProps = teamsFromProps.find(t => Number(t.id) === this.teamId);
    const currentTeam = currentTeamFromProps || Teams.currentTeam();
    const competition = currentTeam && currentTeam.competition
      ? Number(currentTeam.competition)
      : 0;

    // Fetch competition metrics — derive teams and team metrics from it
    // This replaces separate teams/, fetchTeamMetrics, and fetchCompetitionMetrics calls.
    // include=product_awareness returns per-team `promos` used by the
    // Marketing Channel Spend page.
    fetchCompetitionMetrics(competition || null, roundId, null)
      .then(data => {
        // Build teams from MetricsReport. Also pin down the user's
        // competition from the response in case we didn't already have
        // it (falls through to the loop's resolvedCompetition).
        const teamsFromMetrics = [];
        let resolvedCompetition = competition;
        data.forEach(row => {
          if (row.team_name && !teamsFromMetrics.find(t => t.id === row.team)) {
            teamsFromMetrics.push({
              id: row.team,
              name: row.team_name,
              competition: row.competition
            });
          }
          if (Number(row.team) === this.teamId && row.competition) {
            resolvedCompetition = Number(row.competition);
          }
        });

        // Guard against the "no competition filter" case where the
        // metrics endpoint returned every team in the game. Team colors
        // are derived from sort-by-id position within the array, so a
        // list contaminated with other competitions' teams shifts the
        // user's position and gives them a different color than the
        // topbar (which is already competition-scoped).
        const competitionTeams = resolvedCompetition > 0
          ? teamsFromMetrics.filter(t => Number(t.competition) === Number(resolvedCompetition))
          : teamsFromMetrics;

        if (competitionTeams.length > 0) {
          const { reactLocalStorage } = require('reactjs-localstorage');
          reactLocalStorage.setObject("teams_data", competitionTeams);
          // Refresh the shared competition color-index map so every
          // downstream chart resolves team colors from the same source
          // as the topbar (which was seeded at login by Teams.setData).
          persistTeamColorIndexMap(competitionTeams);
        }

        // Extract team-specific metrics (replaces fetchTeamMetrics)
        const rawMetrics = data.find(i => Number(i.team) === this.teamId) || null;
        const metrics = rawMetrics ? this.getCompetitionMetrics(rawMetrics) : null;

        const mappedData = data.map(i => this.getCompetitionMetrics(i));
        this.setState({
          competitionMetrics: mappedData,
          competitiveAnalysis: mappedData,
          competitiveAnalysisAll: mappedData,
          metricsReports: data,
          teams: competitionTeams.length > 0 ? competitionTeams : this.state.teams,
          metrics,
        });

        // Now fetch top_performers and targets with the correct competition
        batchRequest([
          `${process.env.REACT_APP_API_URL}games/${gameId}/top_performers/?round_number=${round}&competition=${resolvedCompetition}`,
          `${process.env.REACT_APP_API_URL}v2/games/targets/?round_number=${round}&course=${Auth.getCourseId()}`,
        ]).then(res => {
          const targetObj = res[1].body ? res[1].body.find(i => i.round_number === round) : null;
          this.setState({
            topPerformers: res[0].body,
            targets: targetObj || null,
          });
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchCompetitionMetrics");
      });

    this.fetchTeamGradesForInstructor(this.getRound());

    // Use rounds from general-info props instead of separate API call
    this.setState({ tmpRounds: this.props.rounds || [] });
  };

  getCompetitionMetrics = data => {
    let result = {};

    Object.keys(data).forEach(key => {
      const ignores = [
        "id",
        "round",
        "created_at",
        "updated_at",
        "is_actual",
        "reason"
      ];
      const convert = {
        team: "team",
        round_number: "Round",
        salesrep: "SalesRep",
        roa: "ROA",
        roe: "ROE",
        ros: "ROS",
        roic: "ROIC",
        capm: "CAPM",
        ebitda: "EBITDA",
        rnoa: "RNOA",
        wacc: "WACC",
        market_demand_auto: "Market Demand - Auto",
        market_demand_mobile: "Market Demand - Mobile",
        average_unit_costs: "Average Unit Cost",
        market_value: "Market Capitalization",
        avg_revenues_per_unit_auto: "Average Selling Price - Auto",
        avg_revenues_per_unit_mobile: "Average Selling Price - Mobile",
        contribution_margin_percent: "Contribution Margin %",
        depreciation_sales_related: "Depreciation-Sales Related",
        equity_cost_of_capital: "Equity Cost of Capital",
        oh_cost_per_unit: "OH Cost Per Unit",
        earnings_per_share: "Earnings Per Share",
        dividends_per_share: "Dividends Per Share",
        contribution_margin_per_unit: "Contribution Margin Per Unit",
        gross_margin_per_unit: "Gross Margin Per Unit",
        price_to_ebitda: "Price to EBITDA",
        profit_margin_percent: "Profit Margin %",
        required_rate_of_return: "Required Rate of Return",
        sgna_costs_percent_sales: "SG&A Costs % Sales"
      };
      const ucFirst = str => str[0].toUpperCase() + str.slice(1);
      const newKey = ignores.includes(key)
        ? key
        : key in convert
          ? convert[key]
          : key
            .split("_")
            .map((i, idx) => (["to", "of", "per", "and", "in"].includes(i) && idx > 0 ? i : ucFirst(i)))
            .join(" ");

      if (!["game", "competition"].includes(key)) {
        result[newKey] = data[key];
      }
    });

    // Compute combined Market Demand from auto + mobile (old competitive_analysis had this)
    if (result["Market Demand - Auto"] != null || result["Market Demand - Mobile"] != null) {
      result["Market Demand"] = (result["Market Demand - Auto"] || 0) + (result["Market Demand - Mobile"] || 0);
    }

    return result;
  };

  fetchCompetitionMetricsForInstructor = () => {
    const { teams } = this.state;
    const team = teams.find(i => i.id === this.teamId);

    if (team) {
      const { competition } = team;

      getRequest(
        `${process.env.REACT_APP_API_URL}v2/finance/metricsreports/?competition=${competition}&round=${this.getRoundId()}`
      )
        .then(res => {
          this.setState({
            competitionMetrics: res.body.results.map(i => this.getCompetitionMetrics(i)),
          });
        })
        .catch(err => { });
    }
  };

  fetchRoundsForInstructor = () => {
    getRequest(`${process.env.REACT_APP_API_URL}game/rounds/`)
      .then(res => {
        const game_id = parseInt(getQueryVariable("game_id"), 10);

        this.setState({
          tmpRounds: res.body.filter(i => i.game === game_id)
        });
      })
      .catch(err => { });
  };

  fetchTeamGradesForInstructor = round => {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/grading/?round_number=${round}&team_id=${this.teamId}`
    )
      .then(res => {
        // Drop late responses whose round no longer matches the URL/
        // state — otherwise a stale in-flight request (e.g. mount fired
        // before App.js parsed ?round= from a new URL) can clobber the
        // good data with "Unknown Rank" / 0.
        if (Number(round) !== Number(this.getRound())) return;
        let tmpTeamGrade = 0;
        let tmpRankName = "Unknown Rank";

        const obj = res.body.find(i => i.round_number === round);
        if (obj) {
          tmpTeamGrade = obj.weighted_value * 100;
          if (obj.ranking !== "") {
            tmpRankName = obj.ranking;
          }
        }

        this.setState({
          grades: res.body,
          tmpTeamGrade,
          tmpRankName
        });
      })
      .catch(err => { });
  };

  fetchData(round) {
    const apiCalls = APICalls(
      Auth.getPosition() === "Student"
        ? Auth.getGameId()
        : parseInt(getQueryVariable("game_id"), 10)
    );
    const { rounds } = this.props;

    if (Auth.getPosition() !== "Student") {
      if (!rounds || rounds.length === 0) {
        return; // Wait for componentWillReceiveProps when rounds load
      }
      const roundId = this.getRoundId();
      if (!roundId) {
        return; // Round not found in rounds array yet
      }
      this.fetchDataForInstructor(apiCalls);
      return;
    }

    if (!rounds || rounds.length === 0) {
      return;
    }
    // Use the picker's current round as-is. The former maxRound clamp
    // against props.rounds dropped lte back to Round 1 whenever the
    // user viewed a later year that hadn't finished yet, blanking all
    // three financial statement panels.
    const roundFinancial = Number(round) || 0;
    const roundObj = rounds.find(i => Number(i.number) === round);
    const roundId = roundObj ? Number(roundObj.id) : 0;
    batchRequest(
      map(
        apiCalls,
        call =>
          `${process.env.REACT_APP_API_URL}${call.url}` +
          {
            false: () => "",
            undefined: () => `/?round_number=${round}`,
            id: () => `/?round=${roundId}`,
            attachCompetition: () => `/?round_number=${round}`,
          }[call.round]()
      )
    ).then(res => {
      const newState = fromPairs(
        map(zip(res, apiCalls), ([{ body }, { key }]) => [
          key,
          body
        ])
      );
      try {
        newState.targets = newState.targets.find(i => i.round_number === round);
      } catch (err) {
        newState.targets = null;
      }
      this.setState(newState);
    });

    // Fetch team decisions data (replaces manufacture_costs, inventory_balances,
    // unit_sales, products, price_forecasts, equities, stock_repurchased, ceo)
    fetchTeamDecisions(round, 'team-reports')
      .then(data => {
        this.setState({
          manufactureCosts: data.manufacture_costs || [],
          inventoryBalances: data.inventory_balances || [],
          unitSales: data.unit_sales || [],
          products: data.products || [],
          forecasts: data.price_forecasts || [],
          equities: data.equities || [],
          stockRepurchased: data.repurchased_shares || data.stock_repurchased || [],
          preMoneys: data.pre_moneys || [],
          equityCumulative: data.equity_cumulative || [],
          ceo: data.decisions ? { team: data.decisions } : {},
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchTeamDecisions");
      });

    // Fetch financial statements (replaces income_statement, balance_sheet, cashflow, projections, financial_analysis)
    fetchFinancialStatements({ roundLte: roundFinancial })
      .then(data => {
        const currentRoundNumber = Number(this.getRound()) || 0;
        const incomeStatement = data.incomeStatement.find(i => Number(i.Round) === currentRoundNumber) || null;
        const balanceSheet = data.balanceSheet.find(i => Number(i.Round) === currentRoundNumber) || null;
        const cashFlows = data.cashflow.find(i => Number(i.Round) === currentRoundNumber) || null;
        this.setState({
          incomeStatement,
          balanceSheet,
          cashFlows,
          projections: data.projections || [],
          financialAnalysis: data.financialAnalysis || [],
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchFinancialStatements");
      });

    this.fetchStaticData(round);
    this.fetchTeamGrades(round);

    // Fetch metrics via MetricsService (cached, avoids duplicate calls from child pages)
    const currentTeam = Teams.currentTeam();
    const competitionId = currentTeam ? Number(currentTeam.competition) : 0;
    const teamId = currentTeam ? Number(currentTeam.id) : 0;

    fetchCompetitionMetrics(competitionId, roundId, null)
      .then(data => {
        // Build teams from MetricsReport for chart colors
        const teamsFromMetrics = [];
        data.forEach(row => {
          if (row.team_name && !teamsFromMetrics.find(t => t.id === row.team)) {
            teamsFromMetrics.push({
              id: row.team,
              name: row.team_name,
              competition: row.competition
            });
          }
        });
        // Update localStorage so Teams.competitiveColors() has all competition teams
        if (teamsFromMetrics.length > 0) {
          const { reactLocalStorage } = require('reactjs-localstorage');
          reactLocalStorage.setObject("teams_data", teamsFromMetrics);
          persistTeamColorIndexMap(teamsFromMetrics);
        }
        const mappedData = data.map(i => this.getCompetitionMetrics(i));

        // Extract team-specific metrics (replaces fetchTeamMetrics)
        const rawMetrics = data.find(i => Number(i.team) === teamId) || null;
        const metrics = rawMetrics ? this.getCompetitionMetrics(rawMetrics) : null;

        this.setState({
          competitionMetrics: mappedData,
          competitiveAnalysis: mappedData,
          competitiveAnalysisAll: mappedData,
          metricsReports: data,
          teams: teamsFromMetrics.length > 0 ? teamsFromMetrics : this.state.teams,
          metrics,
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchCompetitionMetrics");
      });
    //this.fetchCEO(round)
    /*
    this.fetchBalance()
    this.fetchCompetitionMetrics(round)
    this.fetchTeams(round)
    this.fetchMetrics(round)
    this.fetchIncomeStatement(round)
    this.fetchBalanceSheet(round)
    this.fetchCashFlows(round)
    this.fetchTotalDemand(round)
    this.fetchTeamGradesLte()
    this.fetchCompetitiveAnalysis(round)
    this.fetchCompetitiveAnalysisAll(round)
    this.fetchFinancialAnalysis(round)
    this.fetchProjections()
    this.fetchProducts(round)
    this.fetchForecasts(round)
    this.fetchPromos(round)
    this.fetchSales(round)
    this.fetchFinancials(round)
    this.fetchMessages(round)
    this.fetchManufactureCosts(round)
    this.fetchTopPerformers(round)
    this.fetchUnitSales(round)
    this.fetchEquities(round)
    this.fetchStockRepurchased(round)
    this.fetchComparableCompanies(round)
    this.fetchInventoryBalances(round)
    */
  }

  getRound() {
    const { currentRound, roundInUrl } = this.state;

    return roundInUrl || currentRound;
  }

  getRoundId() {
    const { rounds } = this.props;
    const roundNumber = this.getRound();
    const round = rounds.find(i => Number(i.number) === roundNumber);

    return round ? Number(round.id) : 0;
  }

  getCountOfRounds() {
    const { tmpRounds } = this.state;
    const { rounds } = this.props;

    return Auth.getPosition() === "Student" ? rounds.length : tmpRounds.length;
  }

  componentDidMount() {
    if (Auth.isAnyInstructor()) {
      const { rounds } = this.props;
      const roundNumber = Number(this.getRound());

      const activeRound = rounds.find(item => item.number === roundNumber);
      const isActiveRoundFinished = activeRound ? activeRound.is_finished : false;

      if (!isActiveRoundFinished) {
        changeLocation(
          `/decisions/ceo?round=${roundNumber}`
        );
      }
    }
    this.fetchData(this.getRound());
  }

  fetchStaticData(round) {
    // team-reports scope returns constants as an object (not a
    // single-element array) and pre-filters total_demand to the
    // requested round, so no [0]-indirection or per-row round filter
    // downstream in extendedState.
    fetchAllStaticData(round, 'team-reports').then(data => {
      this.setState({
        constants: data.constants,
        totalDemand: (data.totalDemand && data.totalDemand[0]) || null,
        comparableCompanies: data.comparableCompanies,
      });
    });
  }

  fetchManufactureCosts(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/manufacture_costs/?round_number=${round}`
    ).then(res => {
      let avgMaterialCostsPerUnit = 0;
      let avgLaborCostsPerUnit = 0;
      let avgOverheadCostsPerUnit = 0;

      res.body.forEach(product => {
        avgMaterialCostsPerUnit += product.material_costs;
        avgLaborCostsPerUnit += product.labor_costs;
        avgOverheadCostsPerUnit += product.overhead_costs;
      });

      this.setState({
        avgMaterialCostsPerUnit,
        avgLaborCostsPerUnit,
        avgOverheadCostsPerUnit
      });
    });
  }

  fetchTotalDemand(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}static/total_demand/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        totalDemand: body
      });
    });
  }

  fetchInventoryBalances(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/inventory_balances/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        inventoryBalances: body
      });
    });
  }

  fetchComparableCompanies(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}static/comparable_companies/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        comparableCompanies: body
      });
    });
  }

  fetchTopPerformers(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL
      }games/${Auth.getGameId()}/top_performers/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        topPerformers: body
      });
    });
  }

  fetchEquities(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/equities/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        equities: body
      });
    });
  }

  fetchStockRepurchased(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/stock_repurchased/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        stockRepurchased: body
      });
    });
  }

  fetchMetrics(round) {
    const team = Teams.currentTeam();
    const teamId = team ? Number(team.id) : 0;
    const { rounds } = this.props;
    const roundObj = rounds.find(i => Number(i.number) === round);
    const roundId = roundObj ? Number(roundObj.id) : 0;

    fetchTeamMetrics(teamId, roundId).then(function(results) {
      this.setState({
        metrics: results[0]
      });
    }.bind(this));
  }

  fetchFinancialAnalysis(round) {
    const { rounds } = this.props;
    const maxRound = rounds[rounds.length - 1].number;
    const activeRound = round + 1 > maxRound ? maxRound : round + 1;

    getRequest(
      `${process.env.REACT_APP_API_URL}team/financial_analysis/?round_lte=${activeRound}`
    ).then(({ body }) => {
      this.setState({
        financialAnalysis: body
      });
    });
  }

  fetchTeams() {
    getRequest(teamsEndpoint()).then(({ body }) => {
      this.setState({
        teams: body
      });
    });
  }

  fetchCompetitionMetrics(round) {
    const { rounds } = this.props;
    const roundObj = rounds.find(i => Number(i.number) === Number(round));
    const roundId = roundObj ? roundObj.id : null;
    const currentTeam = Teams.currentTeam();
    const competitionId = currentTeam ? currentTeam.competition : null;

    if (!competitionId || !roundId) {
      return;
    }

    fetchCompetitionMetrics(competitionId, roundId).then((data) => {
      this.setState({
        competitionMetrics: data
      });
    });
  }

  fetchUnitSales(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/unit_sales/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        unitSales: body
      });
    });
  }

  fetchCEO(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}team/page/ceo/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        ceo: body
      });
    });
  }

  fetchMessages(round) {
    getRequest(
      `${process.env.REACT_APP_API_URL}teams/messages/?round_number=${round}`
    ).then(({ body }) => {
      this.setState({
        messages: body
      });

      if (body["type_name"]) {
        if (
          body.minimum_requirement_fail ||
          body.price_range_fail ||
          body.distribution_fail
        ) {
          this.setState({
            conditionalPage: true
          });
        }
      }
    });
  }

  getIncomeStatement(round) {
    return getRequest(
      `${process.env.REACT_APP_API_URL}team/report/income_statement/?round_number=${round}`
    );
  }

  fetchIncomeStatement = round => {
    this.getIncomeStatement(round)
      .then(res => {
        this.setState({
          incomeStatement: res.body[0]
        });
      })
      .catch(err => {
        console.log("income err", err);
      });
  };

  getBalanceSheet(round) {
    return getRequest(
      `${process.env.REACT_APP_API_URL}team/report/balance_sheet/?round_number=${round}`
    );
  }

  fetchBalanceSheet = round => {
    this.getBalanceSheet(round)
      .then(res => {
        this.setState({
          balanceSheet: res.body[0]
        });
      })
      .catch(err => {
        console.log("balanceSheet err", err);
      });
  };

  getCashFlows(round) {
    return getRequest(
      `${process.env.REACT_APP_API_URL}team/report/cashflow/?round_number=${round}`
    );
  }

  fetchCashFlows = round => {
    this.getCashFlows(round)
      .then(({ body }) => {
        this.setState({
          cashFlows: body[0]
        });
      })
      .catch(err => {
        console.log("cashFlow err", err);
      });
  };

  fetchBalance = round => {
    if (round) {
      const url = `${process.env.REACT_APP_API_URL}team/page/financials/?round_number=${round}`;

      if (this.balanceRequest) {
        this.balanceRequest.abort();
        this.balanceRequest = null;
      }
      this.balanceRequest = getRequest(url);

      this.balanceRequest
        .then(({ body: { team } }) => {
          this.setState({
            team
          });
        })
        .catch(err => {
          logError(err, "Analysis", "fetchBalance");
        });
    }
  };

  fetchTeamGrades = round => {
    if (round) {
      getRequest(`${process.env.REACT_APP_API_URL}team/grading/?round_number=${round}`)
        .then(({ body }) => {
          // Drop late responses whose round no longer matches the URL/
          // state — otherwise a stale in-flight request (e.g. mount fired
          // before App.js parsed ?round= from a new URL) can clobber the
          // good data with "Unknown Rank" / 0.
          if (Number(round) !== Number(this.getRound())) return;
          let tmpTeamGrade = 0;
          let tmpRankName = "Unknown Rank";

          const obj = body.find(i => i.round_number === round);
          if (obj) {
            tmpTeamGrade = obj.weighted_value * 100;
            if (obj.ranking !== "") {
              tmpRankName = obj.ranking;
            }
          }

          this.setState({
            grades: body,
            tmpTeamGrade,
            tmpRankName
          });
        })
        .catch(err => {
          logError(err, "Analysis", "fetchTeamGrades");
        });
    }
  };

  fetchTeamGradesLte = round => {
    if (round) {
      const url = `${process.env.REACT_APP_API_URL}team/grading/?round_lte=${round}`;

      if (this.teamGradeLteRequest) {
        this.teamGradeLteRequest.abort();
        this.teamGradeLteRequest = null;
      }
      this.teamGradeLteRequest = getRequest(url);

      this.teamGradeLteRequest
        .then(({ body }) => {
          this.setState({
            gradesLte: body
          });
        })
        .catch(err => {
          logError(err, "Analysis", "fetchTeamGradesLte");
        });
    }
  };

  fetchCompetitiveAnalysis = round => {
    const currentTeam = Teams.currentTeam();
    const competitionId = currentTeam ? Number(currentTeam.competition) : 0;
    const { rounds } = this.props;
    const roundObj = (rounds || []).find(i => Number(i.number) === round);
    const roundId = roundObj ? Number(roundObj.id) : 0;

    fetchCompetitionMetrics(competitionId, roundId)
      .then(data => {
        this.setState({
          competitiveAnalysis: data.map(i => this.getCompetitionMetrics(i))
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchCompetitiveAnalysis");
      });
  };

  fetchCompetitiveAnalysisAll = round => {
    const currentTeam = Teams.currentTeam();
    const competitionId = currentTeam ? Number(currentTeam.competition) : 0;

    fetchCompetitionMetrics(competitionId || null, null)
      .then(data => {
        this.setState({
          competitiveAnalysisAll: data.map(i => this.getCompetitionMetrics(i))
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchCompetitiveAnalysisAll");
      });
  };

  fetchProjections = () => {
    const url = `${process.env.REACT_APP_API_URL}team/projections`;

    if (this.projectionsRequest) {
      this.projectionsRequest.abort();
      this.projectionsRequest = null;
    }
    this.projectionsRequest = getRequest(url);

    this.projectionsRequest
      .then(({ body }) => {
        this.setState({
          projections: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchProjections");
      });
  };

  fetchProducts = round => {
    const url = `${process.env.REACT_APP_API_URL}team/products?round_number=${round}`;

    if (this.productsRequest) {
      this.productsRequest.abort();
      this.productsRequest = null;
    }
    this.productsRequest = getRequest(url);

    this.productsRequest
      .then(({ body }) => {
        this.setState({
          products: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchProducts");
      });
  };

  fetchForecasts = round => {
    const url = `${process.env.REACT_APP_API_URL}team/price_forecasts?round_number=${round}`;

    if (this.forecastsRequest) {
      this.forecastsRequest.abort();
      this.forecastsRequest = null;
    }
    this.forecastsRequest = getRequest(url);

    this.forecastsRequest
      .then(({ body }) => {
        this.setState({
          forecasts: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchForecasts");
      });
  };

  fetchPromos = () => {
    const url = `${process.env.REACT_APP_API_URL}team/promos`;

    if (this.promosRequest) {
      this.promosRequest.abort();
      this.promosRequest = null;
    }
    this.promosRequest = getRequest(url);

    this.promosRequest
      .then(({ body }) => {
        this.setState({
          promos: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchPromos");
      });
  };

  fetchSales = () => {
    const url = `${process.env.REACT_APP_API_URL}team/sales`;

    if (this.salesRequest) {
      this.salesRequest.abort();
      this.salesRequest = null;
    }
    this.salesRequest = getRequest(url);

    this.salesRequest
      .then(({ body }) => {
        this.setState({
          sales: body
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchSales");
      });
  };

  fetchFinancials = round => {
    const url = `${process.env.REACT_APP_API_URL}team/page/financials/?round_number=${round}`;

    if (this.financialsRequest) {
      this.financialsRequest.abort();
      this.financialsRequest = null;
    }
    this.financialsRequest = getRequest(url);
    this.setState({ financials: null });
    this.financialsRequest
      .then(({ body: { team } }) => {
        this.setState({
          financials: team
        });
      })
      .catch(err => {
        logError(err, "Analysis", "fetchFinancials");
      });
  };

  get activeRound() {
    const { currentRound, roundInUrl } = this.props;

    return roundInUrl || currentRound;
  }

  render() {
    const { teamId } = this;
    const { location, params, rounds } = this.props;
    const {
      totalDemand,
      competitiveAnalysis,
      teams,
      tmpTeamGrade,
      tmpRankName,
      tmpRounds
    } = this.state;

    const activeRound = this.activeRound;
    const currentRound = rounds.find(i => Number(i.number) === Number(activeRound));

    if (isNil(rounds) && Auth.getPosition() === "Student") {
      return <Loader type="ball-scale-ripple" active />;
    }
    const round = this.getRound();
    const selectedRound = find(rounds, rnd => rnd.number === round);
    if (isNil(selectedRound) && Auth.getPosition() === "Student") {
      return <Loader type="ball-scale-ripple" active />;
    }
    if (isNil(competitiveAnalysis) && Auth.getPosition() !== "Student") {
      return <Loader type="ball-scale-ripple" active />;
    }
    if (currentRound.results_viewable === false && Auth.getPosition() === "Student") {
      return <RoundLockedScreen.Wrapper>
        <RoundLockedScreen.Image alt="Round results locked" src={IconLock} />
        <RoundLockedScreen.Title>Round Results Locked</RoundLockedScreen.Title>
        <RoundLockedScreen.Text>
          The instructor has locked the results for this round. Check back when they reopen it.
        </RoundLockedScreen.Text>
      </RoundLockedScreen.Wrapper>;
    }
    const isFinished =
      Auth.getPosition() === "Student" ? selectedRound.is_finished : true;
    const report1 = isNil(params.report1) ? "" : params.report1;
    const report2 = isNil(params.report2) ? "" : params.report2;
    const hasAccess =
      report1 === "financial-statements" ||
        report1 === "" ||
        (report1 !== "financial-statements" && isFinished === true)
        ? true
        : false;
    const active = report2 !== "" ? report2 : report1;
    const competitionTeams = teams && getCompetitionTeams(teams, teamId);
    const currentRounds = rounds ? rounds : tmpRounds;
    const roundIdObj = currentRounds.find(r => r.number === round);
    const roundId = roundIdObj ? roundIdObj.id : null;
    const extState = {
      ...this.state,
      teams: competitionTeams,
      round,
      roundId,
      countOfRounds: this.getCountOfRounds(),
      teamId,
      teamRank: tmpRankName,
      teamGrade: tmpTeamGrade,
      // totalDemand is now the pre-filtered single row from
      // fetchStaticData; no per-round find needed.
      totalDemand,
      competitiveAnalysisTeam: getTeam(teamId, competitiveAnalysis),
      rounds: currentRounds,
      isFinished: isFinished,
      location: location
    };
    const ifLoaded = (fields, Component) =>
      some(
        fields.filter(i => i !== "roundInUrl"),
        field => isNil(extState[field])
      ) ? (
        <Loader type="ball-scale-ripple" active />
      ) : (
        <Component {...pick(extState, fields)} />
      );

    return (
      <div className="dashboard">
        {Auth.getPosition() === "Student" && (
          <BreadCrumbs
            crumbs={[
              { label: "Home", location: "/start" },
              { label: "Analysis", location: "/analysis" },
              { label: "Team Reports", location: "/analysis/team-reports" }
            ]}
          />
        )}
        <div style={{ display: "flex", flexDirection: "row" }}>
          {[
            <div key={0} className="team-reports-menu">
              <Menu
                location={location.pathname}
                search={location.search}
                round={this.getRound()}
                messages={this.state.messages}
                isFinished={isFinished}
                targets={this.state.targets}
              />
            </div>,
            <Content
              key={1}
              ifLoaded={ifLoaded}
              active={active}
              hasAccess={hasAccess}
            />
          ]}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/analysis/Analysis.js